import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import '../styles/AdminDashboard.css';
import FormatUrl from '../../../utils/UrlFormatter';
import { toast } from "react-toastify";

const InvoicePreview = ({ selectedEvent, clinicId, onConfirm, isInvoiceAlreadyGenerated }) => {
  const [duration, setDuration] = useState('');
  const [amount, setAmount] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  const [insuranceList, setInsuranceList] = useState([]);
  const [selectedInsuranceId, setSelectedInsuranceId] = useState('');
  const [paidByInsurance, setPaidByInsurance] = useState('');
  const [paidByPatient, setPaidByPatient] = useState('');
  const [insuranceClaimId, setInsuranceClaimId] = useState('');
  const [selectedInsuranceLabel, setSelectedInsuranceLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showZeroInsuranceModal, setShowZeroInsuranceModal] = useState(false);
  const [isZeroInsuranceAcknowledged, setIsZeroInsuranceAcknowledged] = useState(false);

  const parsedAmount = parseFloat(amount) || 0;
  const parsedDuration = parseInt(duration) || 0;
  const taxAmount = Number((parsedAmount * taxRate).toFixed(2));
  const totalAmount = Number((parsedAmount + taxAmount).toFixed(2));

  const insuranceOptions = [
    { value: '', label: 'None' },
    ...insuranceList.map((obj) => {
      const [key, label] = Object.entries(obj)[0];
      return { value: key, label };
    }),
  ];
  

  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      try {
        const csrfResponse = await fetch(FormatUrl(`/csrf-token/`), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        if (!csrfResponse.ok) throw new Error('Failed to fetch CSRF token.');
        const { csrfToken } = await csrfResponse.json();

        const apiUrl = FormatUrl(`/patient-invoice-details/?appointment_id=${selectedEvent.id}`);
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          credentials: 'include',
        });

        if (!response.ok) throw new Error('Failed to fetch invoice details.');
        const data = await response.json();

        setDuration(data.duration?.toString() || '');
        const rawAmount = data.amount || data.ammount || '';
        setAmount(parseFloat(rawAmount).toFixed(2));
        setTaxRate(parseFloat(data.tax_rate || 0));
        setInsuranceList(data.insurance_list || []);
        setPaidByInsurance(data.paid_by_insurance || '');
        setPaidByPatient(data.paid_by_patient || '');
        setInsuranceClaimId(data.insurance_claim_id || '');

        if (data.selected_insurance_company) {
          setSelectedInsuranceLabel(data.selected_insurance_company);
          const found = (data.insurance_list || []).find(
            obj => Object.values(obj)[0] === data.selected_insurance_company
          );
          if (found) {
            const [key] = Object.entries(found)[0];
            setSelectedInsuranceId(key);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Invoice preview load error:', err);
        setError('Unable to load invoice details.');
        setLoading(false);
      }
    };

    if (selectedEvent?.id) {
      fetchInvoiceDetails();
    }
  }, [selectedEvent]);

  useEffect(() => {
    if (!isInvoiceAlreadyGenerated && selectedInsuranceId && paidByInsurance !== '') {
      const insuranceVal = parseFloat(paidByInsurance) || 0;
      const adjusted = totalAmount - insuranceVal;
      setPaidByPatient(adjusted > 0 ? adjusted.toFixed(2) : '0.00');
    }
  }, [paidByInsurance, totalAmount, selectedInsuranceId, isInvoiceAlreadyGenerated]);

  const submitInvoice = async () => {
    try {
      const csrfRes = await fetch(FormatUrl('/csrf-token/'), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
  
      if (!csrfRes.ok) throw new Error('CSRF fetch failed');
      const { csrfToken } = await csrfRes.json();
  
      const formData = new URLSearchParams();
      formData.append('appointment_id', selectedEvent.id);
      formData.append('clinic_id', clinicId);
      formData.append('duration', parsedDuration);
      formData.append('amount', parsedAmount.toFixed(2));
  
      if (selectedInsuranceId) {
        const paidByInsuranceVal = parseFloat(paidByInsurance || '0') || 0;
        const paidByPatientVal = parseFloat(paidByPatient || '0') || 0;
        const expectedTotal = parsedAmount + taxAmount;
        const combined = Number((paidByInsuranceVal + paidByPatientVal).toFixed(2));
  
        if (combined !== expectedTotal) {
          toast.error(`Mismatch: Paid total ($${combined}) must equal invoice total ($${expectedTotal}).`);
          return;
        }
  
        if (!insuranceClaimId.trim()) {
          toast.error("Insurance Claim ID is required when an insurance provider is selected.");
          return;
        }
  
        formData.append('insurance_company_id', String(selectedInsuranceId));
        formData.append('amount_paid_by_insurance', String(paidByInsuranceVal.toFixed(2)));
        formData.append('amount_paid_by_patient', String(paidByPatientVal.toFixed(2)));
        formData.append('insurance_claim_id', insuranceClaimId.trim());
      }
  
      const res = await fetch(FormatUrl('/update-patient-invoice-details/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: formData,
      });
  
      if (!res.ok) throw new Error('Invoice update failed');
      const result = await res.json();
  
      onConfirm({
        appointment_id: selectedEvent.id,
        clinic_id: clinicId,
        duration: parsedDuration,
        amount: parsedAmount,
        tax_rate: taxRate,
        tax_amount: taxAmount,
        total_amount: totalAmount,
        insurance: selectedInsuranceId,
        paid_by_insurance: paidByInsurance,
        paid_by_patient: paidByPatient,
        insurance_claim_id: insuranceClaimId,
        result,
      });
  
      setIsZeroInsuranceAcknowledged(false);
    } catch (err) {
      console.error('Invoice POST failed:', err);
      toast.error('Failed to confirm invoice. Please try again.');
    }
  };
  
  const handleConfirm = () => {
    const paidByInsuranceVal = parseFloat(paidByInsurance || '0') || 0;
  
    if (selectedInsuranceId && paidByInsuranceVal === 0 && !isZeroInsuranceAcknowledged) {
      setShowZeroInsuranceModal(true);
      return;
    }
  
    submitInvoice();
  };
  

  const showInsuranceFields =
    (selectedInsuranceId && selectedInsuranceId !== '') ||
    (isInvoiceAlreadyGenerated && selectedInsuranceLabel && selectedInsuranceLabel !== 'None');

  if (loading) return <p style={{ marginBottom: '1rem' }}>Loading invoice preview...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  return (
    <div className="invoice-preview-box-admin">
      <h4>Invoice Preview</h4>

      <label>Duration (minutes):</label>
      <input
        type="text"
        value={duration}
        onChange={(e) => setDuration(e.target.value)}
        disabled={isInvoiceAlreadyGenerated}
      />

      <label>Amount ($):</label>
      <input
        type="text"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        disabled={isInvoiceAlreadyGenerated}
      />

      <label>Insurance Provider:</label>
      <Select
        classNamePrefix="react-select"
        maxMenuHeight={200}
        value={insuranceOptions.find(opt => opt.value === selectedInsuranceId) || null}
        onChange={(selected) => {
          setSelectedInsuranceId(selected?.value || '');
          setSelectedInsuranceLabel(selected?.label || '');
        }}
        options={insuranceOptions}
        isDisabled={isInvoiceAlreadyGenerated}
        isClearable
        placeholder="Select insurance..."
        menuPortalTarget={document.body}
        styles={{
          menuPortal: base => ({ ...base, zIndex: 9999 }),
          menu: base => ({ ...base, zIndex: 9999 })  // fallback
        }}
      />

      {isInvoiceAlreadyGenerated && selectedInsuranceLabel && (
        <p style={{ fontStyle: 'italic', color: 'var(--clr-grey-2)', marginTop: '-0.5rem' }}>
          Selected: {selectedInsuranceLabel}
        </p>
      )}

      {showInsuranceFields && (
        <>
        <label>Insurance Claim ID: <span style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={insuranceClaimId}
            onChange={(e) => setInsuranceClaimId(e.target.value)}
            disabled={isInvoiceAlreadyGenerated}
          />

          {!isInvoiceAlreadyGenerated && (
            <div className="insurance-hint-text">
              <em>Enter the amount covered by insurance to automatically calculate the patient's remaining payable amount.</em>
            </div>
          )}

          <label>Amount Paid by Insurance ($): <span style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={paidByInsurance}
            onChange={(e) => setPaidByInsurance(e.target.value)}
            disabled={isInvoiceAlreadyGenerated}
          />

          <label>Amount Paid by Patient ($):<span style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={paidByPatient}
            disabled={true}
          />

          
        </>
      )}

      <div className="invoice-summary-admin">
        <p><strong>Tax Rate:</strong> {(taxRate * 100).toFixed(0)}%</p>
        <p><strong>Tax Amount:</strong> ${taxAmount.toFixed(2)}</p>
        <p><strong>Total Amount:</strong> ${totalAmount.toFixed(2)}</p>
      </div>

      {!isInvoiceAlreadyGenerated && (
        <div className="modal-btns-admin">
          <button className="confirm-payment-btn-admin" onClick={handleConfirm}>
            Confirm Invoice
          </button>
        </div>
      )}

      {showZeroInsuranceModal && (
        <div className="zero-insurance-modal-overlay">
          <div className="zero-insurance-modal">
            <p>
              <strong>Amount Paid by Insurance is $0.</strong><br />
              Are you sure you want to proceed?
            </p>
            <div className="dialog-btn-group">
              <button
                onClick={() => {
                  setShowZeroInsuranceModal(false);
                  setIsZeroInsuranceAcknowledged(true);
                  submitInvoice();
                }}
              >
                Yes, Continue
              </button>
              <button onClick={() => setShowZeroInsuranceModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default InvoicePreview;
